import React, { useState } from 'react';
import { Monitor, Smartphone, Link2, X, Wifi } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PosSyncModal = ({ onClose, onConnect, getAuthHeader }) => {
  const [mode, setMode] = useState(null); // null, 'create', 'join'
  const [code, setCode] = useState('');
  const [generatedCode, setGeneratedCode] = useState('');
  const [loading, setLoading] = useState(false);

  const handleCreate = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/pos/sync/create`, {}, { headers: getAuthHeader() });
      setGeneratedCode(res.data.code);
      onConnect(res.data.code);
    } catch (err) {
      toast.error('Error al crear sesión');
    } finally {
      setLoading(false);
    }
  };

  const handleJoin = async () => {
    if (code.length !== 4) { toast.error('Ingresa un código de 4 dígitos'); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/pos/sync/validate/${code}`, {}, { headers: getAuthHeader() });
      onConnect(code);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Código no válido');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="bg-white rounded-2xl w-full max-w-sm p-6" data-testid="pos-sync-modal">
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-bold text-lg flex items-center gap-2">
            <Link2 size={22} className="text-green-600" />
            Sincronizar Dispositivos
          </h3>
          <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-lg" data-testid="close-sync-modal">
            <X size={18} />
          </button>
        </div>

        {!mode && !generatedCode && (
          <div className="space-y-3">
            <p className="text-sm text-gray-500 mb-4">
              Conecta el celular con la laptop para escanear productos desde el celular y cobrar en la laptop.
            </p>
            <button onClick={() => { setMode('create'); handleCreate(); }}
              className="w-full p-4 rounded-xl border-2 border-blue-100 hover:border-blue-300 transition-colors text-left flex items-center gap-4"
              data-testid="sync-create-btn"
            >
              <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                <Monitor size={24} className="text-blue-600" />
              </div>
              <div>
                <p className="font-bold text-sm">Soy la LAPTOP (caja)</p>
                <p className="text-xs text-gray-500">Crear código para que el celular se conecte</p>
              </div>
            </button>
            <button onClick={() => setMode('join')}
              className="w-full p-4 rounded-xl border-2 border-green-100 hover:border-green-300 transition-colors text-left flex items-center gap-4"
              data-testid="sync-join-btn"
            >
              <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                <Smartphone size={24} className="text-green-600" />
              </div>
              <div>
                <p className="font-bold text-sm">Soy el CELULAR (escáner)</p>
                <p className="text-xs text-gray-500">Ingresar código de la laptop</p>
              </div>
            </button>
          </div>
        )}

        {mode === 'create' && generatedCode && (
          <div className="text-center py-4">
            <div className="w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center mx-auto mb-4">
              <Wifi size={32} className="text-blue-600" />
            </div>
            <p className="text-sm text-gray-500 mb-3">Ingresa este código en el celular:</p>
            <div className="text-5xl font-black tracking-[0.3em] text-blue-600 mb-4" data-testid="sync-code-display">
              {generatedCode}
            </div>
            <p className="text-xs text-gray-400">Esperando conexión del celular...</p>
            <div className="mt-4 flex items-center justify-center gap-2 text-green-600 text-sm">
              <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
              Sesión activa
            </div>
          </div>
        )}

        {mode === 'join' && !generatedCode && (
          <div className="py-2">
            <div className="w-14 h-14 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
              <Smartphone size={28} className="text-green-600" />
            </div>
            <p className="text-sm text-gray-500 text-center mb-4">Ingresa el código que aparece en la laptop:</p>
            <input
              type="text"
              inputMode="numeric"
              maxLength={4}
              value={code}
              onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 4))}
              className="w-full text-center text-4xl font-black tracking-[0.3em] py-4 rounded-xl border-2 border-gray-200 focus:border-green-400 focus:outline-none"
              placeholder="----"
              autoFocus
              data-testid="sync-code-input"
            />
            <button
              onClick={handleJoin}
              disabled={code.length !== 4 || loading}
              className="w-full mt-4 py-3 rounded-xl text-white font-bold disabled:opacity-40"
              style={{ background: '#10b981' }}
              data-testid="sync-join-submit"
            >
              {loading ? 'Conectando...' : 'Conectar'}
            </button>
            <button onClick={() => setMode(null)} className="w-full mt-2 py-2 text-sm text-gray-500 hover:text-gray-700">
              Volver
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default PosSyncModal;
